import { forwardRef, memo, useEffect, useId } from "react";
import { createPortal } from "react-dom";
import { autoUpdate, flip, offset, useFloating, UseFloatingOptions, VirtualElement } from "@floating-ui/react";
import classNames from "classnames";
import { useMergedRef } from "../../../utils";
import { useGetFloatingUIPortalElement } from "../../FloatingUIPortal";
import { Overlay, OverlayProps } from "../../Overlay";
import { DropdownList, DropdownListProps } from "../DropdownList";

import styles from "./style.module.scss";

export type DropdownCloseOnClick = "always" | "outOfDropDown" | "never" | "overlay";
export type DropdownProps = DropdownListProps & {
  isOpen?: boolean,
  onRequestClose?: () => void,
  referenceElement?: Element | VirtualElement | null,
  closeOnClick?: DropdownCloseOnClick,
  closeOnWheel?: boolean,
  closeOnEsc?: boolean,
  floatingOptions?: UseFloatingOptions
  overlayProps?: OverlayProps
}

export const Dropdown = memo(forwardRef<any, DropdownProps>((
  {
    isOpen,
    onRequestClose,
    referenceElement,
    children,
    floatingOptions,
    closeOnClick = "always",
    closeOnWheel,
    closeOnEsc = true,
    overlayProps,
    ...props
  },
  ref,
) => {
  const portalElement = useGetFloatingUIPortalElement();
  const reactOverlayId = useId();
  const reactDropdownId = useId();
  const overlayId = overlayProps?.id ?? reactOverlayId;
  const dropdownId = props?.id ?? reactDropdownId;

  const { refs: { setReference, setFloating }, floatingStyles } = useFloating({
    placement: "bottom-start",
    whileElementsMounted: autoUpdate,
    ...floatingOptions,
    middleware: [
      offset(8),
      flip({
        fallbackPlacements: [ "bottom-end", "top-start", "top-end", "bottom-start" ],
      }),
      ...floatingOptions?.middleware || [],
    ],
  });

  useEffect(() => {
    if (isOpen) {
      const wheelListener = (e: WheelEvent) => {
        if (closeOnWheel) {
          onRequestClose?.();
        }
      };
      const keyDownListener = (e: KeyboardEvent) => {
        if (closeOnEsc && e.key === "Escape") {
          onRequestClose?.();
        }
      };

      const bubblingListener = (e: MouseEvent) => {
        if (closeOnClick === "always") {
          onRequestClose?.();
        } else if (closeOnClick === "outOfDropDown") {
          if (e.target) {
            if (!document.getElementById(dropdownId)
              ?.contains(e.target as Node)) {
              onRequestClose?.();
            }
          }
        } else if (closeOnClick === "overlay") {
          if ((e.target as HTMLElement).id === overlayId) {
            onRequestClose?.();
          }
        }
      };
      const capturingListener = () => {
        document.addEventListener("click", bubblingListener);
      };
      document.addEventListener("keydown", keyDownListener);
      document.addEventListener("wheel", wheelListener);
      document.addEventListener("click", bubblingListener, { capture: true });
      return () => {
        document.removeEventListener("keydown", keyDownListener);
        document.removeEventListener("wheel", wheelListener);
        document.removeEventListener("click", bubblingListener);
        document.removeEventListener("click", capturingListener, { capture: true });
      };
    }
  }, [ isOpen, onRequestClose, closeOnClick, closeOnWheel, dropdownId, closeOnEsc, overlayId ]);
  useEffect(() => {
    setReference(referenceElement || null);
  }, [ referenceElement, setReference ]);

  const mergedRef = useMergedRef(ref, setFloating);

  return (
    (portalElement && isOpen) ?
      createPortal(
        <Overlay
          backgroundType="none"
          {...overlayProps}
          style={{
            pointerEvents: closeOnClick === "overlay" ? "all" : undefined,
            ...overlayProps?.style,
          }}
          id={overlayId}
          className={classNames(styles.overlay, overlayProps?.className)}
        >
          <DropdownList
            {...props}
            id={dropdownId}
            className={classNames(styles.dropdown, props.className)}
            ref={mergedRef}
            style={{
              ...props.style,
              ...floatingStyles,
            }}
          >
            {children}
          </DropdownList>
        </Overlay>,
        portalElement,
      )
      : null
  );
}));
